import { useQuery } from "@tanstack/react-query";
import { getUpcomingNPOEvents } from "@/lib/queries/events";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function UpcomingEventsSection({ profile, session }: { profile: any; session: any }) {
  const navigate = useNavigate();
  const { data: response = { data: [] }, isLoading } = useQuery({
    queryKey: ["npo-events", profile?.id, 1],
    queryFn: () => getUpcomingNPOEvents(session?.access_token!, 10, 1),
    enabled: !!profile?.id && !!session?.access_token,
  });

  const events = response.data || [];
  const upcomingEvents = events.filter(
    (event) => !["event_complete", "event_cancelled", "payment_failed"].includes(event.status)
  );

  if (isLoading) {
    return (
      <div className="mt-10 space-y-4">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="mt-10">
      <h2 className="text-2xl font-bold mb-6">Upcoming Events</h2>
      {upcomingEvents.length === 0 ? (
        <div className="text-gray-500 border rounded-lg p-8 text-center bg-background">
          No upcoming events found.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {upcomingEvents.map(event => (
            <Card
              key={event.id}
              className="hover:shadow-lg transition-all cursor-pointer"
              onClick={() => navigate(`/dashboard/events/${event.id}`)}
              tabIndex={0}
              role="button"
              aria-label={`View event ${event.event_name}`}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  navigate(`/dashboard/events/${event.id}`);
                }
              }}
            >
              <CardHeader className="flex flex-row items-center gap-3 pb-2">
                <Calendar className="h-5 w-5 text-primary" />
                <CardTitle className="text-lg font-semibold">{event.event_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600 mb-2 break-words max-w-full">{event.event_date} | {event.event_time}</div>
                <div className="text-sm text-muted-foreground mb-2 break-words max-w-full">{event.location || 'Location TBA'}</div>
                {/* If you display a URL, wrap it with break-words and optionally truncate */}
                {event.link && (
                  <div className="text-green-700 text-sm mb-2 break-words max-w-full">
                    <a href={event.link} target="_blank" rel="noopener noreferrer" className="underline break-all">
                      {event.link}
                    </a>
                  </div>
                )}
                <div className="text-xs text-gray-400 break-words max-w-full">Status: {event.status.replace(/_/g, ' ')}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
